import React, { useEffect, useState } from 'react';
import { Alert, Button, CircularProgress, FormControlLabel, Paper, Switch, TextField } from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { returnOnlyNumbers } from '../helpers/format';
import CnpjInput from '../components/CnpjInput'
import MultipleSelect from '../components/Multiselect';
import api from '../services/api';

function CompanyForm() {
  const { cnpj } = useParams();
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    cnpj: '',
    isActive: true,
    users: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (isMounted) {
        await getAllUsers();
        if (cnpj) {
          await getCompanyByCnpj(cnpj);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const navigateBack = () => navigate('/empresas');

  const getAllUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data);
    } catch (err) {
      setUsers([]);
      showError(err.response?.data?.message || 'Algo de errado aconteceu.');
    }
  };

  const updateCompany = async (cnpj, data) => {
    try {
      setLoading(true);
      await api.patch(`/companies/${cnpj}`, data, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      navigateBack();
    } catch (err) {
      setLoading(false);
      showError(err.response?.data?.message || 'Algo de errado aconteceu.');
    }
  };

  const createNewCompany = async (data) => {
    try {
      setLoading(true);
      await api.post('/companies/register', data);
      navigateBack();
    } catch (err) {
      setLoading(false);
      showError(err.response?.data?.message || 'Algo de errado aconteceu.');
    }
  };

  const getCompanyByCnpj = async (cnpj) => {
    try {
      const response = await api.get(`/companies/${cnpj}`);
      const actualUsers = response.data.data.UserCompanies.map(({ User: { id, name } }) => ({ id, name }));
      delete response.data.data.UserCompanies;
      setFormData({
        ...response.data.data,
        users: actualUsers,
      });
    } catch (err) {
      showError(err.response?.data?.message || 'Algo de errado aconteceu.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: checked }));
  };

  const handleSubmit = async () => {
    const data = {
      name: formData.name,
      cnpj: returnOnlyNumbers(formData.cnpj),
      isActive: formData.isActive,
      users: formData.users.map(({ id }) => id),
    };

    if (cnpj) {
      delete data.cnpj;
      await updateCompany(cnpj, data);
    } else {
      await createNewCompany(data);
    }
  };

  const showError = (message) => {
    const alert = <Alert
      className="error-content"
      variant="outlined"
      severity="error"
    >
      {message}
    </Alert>;
    setError(alert);
    setTimeout(() => setError(null), 3000);
  };

  return (
    <div>
      <div style={{ margin: '21.44px 0'}}>
        <Link className="color-white" to="/empresas">Empresas</Link>
        <span> / </span>
        <h1 className="inline">{ cnpj ? 'Editar' : 'Novo' }</h1>
      </div>
      <div className="w-100 h-100 align-center justify-center">
        {error}
        <Paper sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: '2rem',
          gap: '2rem',
        }}>
          <div
            className="w-100"
            style={{
              display: 'flex',
              gap: '.5rem',
              flexDirection: 'column',
            }}
          >
            <div className="w-100 align-center justify-between">
              <h3 style={{ margin: 0 }}>Dados empresa</h3>
              <FormControlLabel
                control={<Switch
                  disabled={loading}
                  name="isActive"
                  onChange={handleSwitchChange}
                  checked={formData.isActive}
                  label=""
                  labelPlacement="left"
                />}
                label="Empresa ativa"
                labelPlacement="start"
              />
            </div>
            <div
              className="w-100"
              style={{
                display: 'flex',
                gap: '.5rem',
              }}
            >
              <TextField
                sx={{ width: '100%' }}
                size="small"
                id="name"
                name="name"
                label="Razão social"
                variant="outlined"
                disabled={loading}
                onChange={handleChange}
                value={formData.name}
              />
              <CnpjInput
                value={formData.cnpj}
                loading={loading}
                onChange={handleChange}
              />
            </div>
          </div>
          <div
            className="w-100"
            style={{
              display: 'flex',
              gap: '.5rem',
              flexDirection: 'column',
            }}
          >
            <h3 style={{ margin: 0 }}>Responsabilidade</h3>
            <div
              className="w-100"
              style={{
                display: 'flex',
                gap: '.5rem',
              }}
            >
              <MultipleSelect
                onChange={(value) => handleChange({ target: { name: 'users', value } })}
                label="Usuários"
                description="name"
                loading={loading}
                data={users}
                defaultData={formData.users}
              />
            </div>
          </div>
          <div className="w-100">
            <Button
              sx={{ width: '150px', height: '40px', marginRight: '.5rem' }}
              variant="outlined"
              color="error"
              onClick={navigateBack}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              sx={{ width: '150px', height: '40px' }}
              variant="outlined"
              color="success"
              onClick={handleSubmit}
              disabled={loading}
            >
              { loading ? <CircularProgress size={20} /> : cnpj ? 'Salvar' : 'Cadastrar' }
            </Button>
          </div>
        </Paper>
      </div>
    </div>
  );
}

export default CompanyForm;
